import { validateEnvironmentBaseUrl } from './environmentBaseUrlValidation';

/** Keep aligned with Environments page / EnvironmentService.ENVIRONMENT_NAME_MAX_LENGTH. */
export const AGENT_ENVIRONMENT_NAME_MAX_LENGTH = 30;

/** Validates environment name for AI Agent create (length matches Projects/Environments pages). */
export function validateAgentEnvironmentName(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Environment name is required';
  if (trimmed.length > AGENT_ENVIRONMENT_NAME_MAX_LENGTH) {
    return `Environment name must be ${AGENT_ENVIRONMENT_NAME_MAX_LENGTH} characters or fewer`;
  }
  return null;
}

/**
 * True when hostname is localhost, an IP, or a multi-label domain (has a dot).
 * Rejects single-label junk like "abc123", "test", "hello".
 * Matches Environments page "sensible host" rule (localhost or contains '.').
 */
export function isValidAgentHttpUrlHost(hostname: string): boolean {
  const host = (hostname || '').trim().toLowerCase();
  if (!host) return false;
  if (host === 'localhost' || host === 'localhost.localdomain') return true;
  // IPv6 (URL.hostname omits brackets)
  if (host.includes(':')) return true;
  // Domain or IPv4 — require at least one dot (e.g. example.com, 192.168.1.1)
  if (!host.includes('.')) return false;
  // Reject placeholder-like hosts that are only dots/empty labels
  if (host.startsWith('.') || host.endsWith('.') || host.includes('..')) return false;
  return true;
}

function validateAgentHttpUrl(
  url: string,
  messages: { required: string; invalid: string; protocol: string },
): string | null {
  const raw = url.trim();
  if (!raw) return messages.required;
  if (/^https?:\/\/\s*$/i.test(raw)) {
    return messages.invalid;
  }
  // Reject non-http(s) schemes before we auto-prefix https://
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw) && !/^https?:\/\//i.test(raw)) {
    return messages.protocol;
  }
  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return messages.protocol;
    }
    if (!isValidAgentHttpUrlHost(parsed.hostname)) {
      return messages.invalid;
    }
    return null;
  } catch {
    return messages.invalid;
  }
}

/** Validates Base URL for AI Agent environment create using Environments Base URL rules. */
export function validateAgentEnvironmentBaseUrl(url: string): string | null {
  const raw = url.trim();
  if (!raw) return 'Base URL is required';
  if (/^https?:\/\/\s*$/i.test(raw)) {
    return 'Please enter a valid URL (e.g. https://api.example.com)';
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw) && !/^https?:\/\//i.test(raw)) {
    return 'Environment URL must start with http:// or https://';
  }
  // Environments rejects hosts with no http:// or https:// (e.g. api.example.com).
  if (!/^https?:\/\//i.test(raw)) {
    return 'URL must start with http:// or https://';
  }
  const candidate = raw;
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return 'Environment URL must start with http:// or https://';
    }
    if (!isValidAgentHttpUrlHost(parsed.hostname)) {
      return 'Please enter a valid URL (e.g. https://api.example.com)';
    }
  } catch {
    // Invalid ports throw here. Environments reports those below.
  }
  const environmentError = validateEnvironmentBaseUrl(candidate);
  if (!environmentError) return null;
  if (environmentError === 'URL must start with http:// or https://') {
    return 'Environment URL must start with http:// or https://';
  }
  return environmentError;
}

/** Validates Swagger/OpenAPI URL for AI Agent import. */
export function validateAgentSwaggerUrl(url: string): string | null {
  return validateAgentHttpUrl(url, {
    required: 'Swagger URL is required',
    invalid: 'Please enter a valid URL (e.g. https://api.example.com/v3/api-docs)',
    protocol: 'Swagger URL must start with http:// or https://',
  });
}

/** Ensures stored base URL includes a protocol when user omits it (e.g. www.example.com). */
export function normalizeAgentEnvironmentBaseUrl(url: string): string {
  const raw = url.trim();
  if (!raw) return raw;
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

/** Ensures Swagger URL includes a protocol when user omits it. */
export function normalizeAgentSwaggerUrl(url: string): string {
  return normalizeAgentEnvironmentBaseUrl(url);
}
